import {
  CaretDownOutlined,
  CaretRightOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import MarkdownIt from 'markdown-it';
import React, { useEffect, useState } from 'react';
import MdEditor from 'react-markdown-editor-lite';
import styles from './index.less';

/**
 * 问答结果项的属性接口
 * @interface QAResultItemProps
 */
interface QAResultItemProps {
  question: string;
  guidelineName?: string;
  thinking?: boolean;
  thinkingContent: string;
  answering?: boolean;
  answer: string;
  collapsed?: boolean;
}

/**
 * 问答结果项组件
 * 展示问答对，包括问题和AI的回答，支持展开/收起AI思考过程
 *
 * @component QAResultItem
 * @param {QAResultItemProps} props - 组件属性
 * @returns {React.ReactElement} 渲染的组件
 */
const QAResultItem: React.FC<QAResultItemProps> = ({
  question,
  thinking,
  guidelineName,
  thinkingContent,
  answering,
  answer,
  collapsed = true,
}) => {
  const mdParser = new MarkdownIt({});
  // 思考内容的折叠状态
  const [isThinkingCollapsed, setIsThinkingCollapsed] = useState(collapsed);
  // 思考中的动态省略号
  const [thinkingDots, setThinkingDots] = useState('');

  // 处理思考中的动态省略号效果
  useEffect(() => {
    let dotsTimer: NodeJS.Timeout;
    if (thinking) {
      dotsTimer = setInterval(() => {
        setThinkingDots((prev) => {
          if (prev.length >= 3) return '';
          return `${prev}.`;
        });
      }, 500);
    }
    return () => {
      clearInterval(dotsTimer);
      setThinkingDots('');
    };
  }, [thinking]);

  return (
    <div className={styles.qaResultItem}>
      <div className={styles.qaQuestion}>
        <span className={styles.qaQuestionText}>{question}</span>
        <span className={styles.qaQuestionIcon} />
      </div>
      <div className={styles.qaAnswer}>
        <div className={styles.qaAnswerHeader}>
          <span className={styles.qaAnswerIcon} />
          {thinkingContent && (
            <div
              className={classNames(styles.thinkingContentTitle, {
                [styles.thinkingActive]: thinking,
              })}
              onClick={() => setIsThinkingCollapsed(!isThinkingCollapsed)}
            >
              <span className={styles.collapseIcon}>
                {isThinkingCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
              </span>
              {thinking ? (
                <span className={styles.thinkingStatus}>
                  <LoadingOutlined style={{ marginLeft: 4, fontSize: 14 }} spin />{' '}
                  <span>思考中{thinkingDots}</span>
                </span>
              ) : (
                '已深度思考'
              )}
            </div>
          )}
        </div>
        <div className={styles.qaAnswerContent}>
          {thinkingContent && (
            <div
              className={classNames(styles.thinkingContentWrapper, {
                [styles.collapsed]: isThinkingCollapsed,
                [styles.expanded]: !isThinkingCollapsed,
              })}
            >
              <MdEditor
                value={thinkingContent}
                readOnly
                htmlClass={classNames(
                  'custom-html-style',
                  styles['editor-html-style'],
                  styles['thinking-content'],
                )}
                markdownClass={classNames(styles['editor-md-style'])}
                view={{
                  menu: false,
                  md: false,
                  html: true,
                }}
                renderHTML={(text) => mdParser.render(text)}
              />
            </div>
          )}
          {answer && (
            <MdEditor
              value={answer}
              readOnly
              htmlClass={classNames(
                'custom-html-style',
                styles['editor-html-style'],
                styles.qaAnswerText,
              )}
              markdownClass={classNames(styles['editor-md-style'])}
              view={{
                menu: false,
                md: false,
                html: true,
              }}
              renderHTML={(text) => mdParser.render(text)}
            />
          )}
          <div className={styles.qaAnswerMeta}>
            {guidelineName && <span className={styles.qaTimeStamp}>来源：{guidelineName}</span>}
            {(thinkingContent || answer) && !answering && (
              <span className={styles.qaAnswerInfo}>
                <ExclamationCircleOutlined className={styles.infoIcon} />
                本回答由AI生成，内容仅供参考
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QAResultItem;
